import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { RESOLUTION_TO_WIDTH_HEIGHT_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const veo31FastParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16"]).default("9:16"),
    resolution: z.enum(["720p", "1080p"]).default("720p"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

function getVeo31FastCostPerSecond(): number {
  // fal.ai charges $0.10/s (no audio) or $0.15/s (with audio) for all resolutions
  // This model has no audio param, so we use the base rate
  const costPerSecond = 0.1;

  return 1_000_000 * costPerSecond * (1 + BRAND_MARKUP);
}

// Vertex AI always generates at 720p — no resolution parameter supported
const VEO31_VERTEX_COST_PER_SECOND = 1_000_000 * 0.08 * (1 + BRAND_MARKUP);

export const veo31Fast = defineModel({
  key: "veo3_1-fast",
  stepType: "video",
  spec: {
    label: "Veo 3.1 Fast",
    description:
      "One of the best video & audio models, with added scene control",
    icon: "gemini-icon.svg",
    tags: ["video-gen"],
    featured: true,
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      aspectRatio: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "First Frame",
          slot: "first_frame",
          required: false,
        },
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Last Frame",
          slot: "last_frame",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
        ],
      },
      resolution: {
        items: [
          { label: "720p", value: "720p" },
          { label: "1080p", value: "1080p" },
        ],
      },
    },
    params: veo31FastParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/veo3.1/fast",
    entries: [
      { unit: "per_second", unitPriceMicrodollars: 100_000, variant: "720p" },
      { unit: "per_second", unitPriceMicrodollars: 100_000, variant: "1080p" },
    ],
  },
  providers: [
    {
      provider: "runware",
      modelId: "google:3@3",
      estimateCost(_params) {
        return Math.ceil(getVeo31FastCostPerSecond() * 8);
      },
      estimateCostPerSecond(_params) {
        return getVeo31FastCostPerSecond();
      },
      costPerSecond: 0.1 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const resMap = RESOLUTION_TO_WIDTH_HEIGHT_MAP[params.resolution];
        const size = resMap[params.aspectRatio];

        const firstFrame = params.attachments?.find(
          (attachment) => attachment.slot === "first_frame",
        )?.data;

        const lastFrame = params.attachments?.find(
          (attachment) => attachment.slot === "last_frame",
        )?.data;

        if (lastFrame && !firstFrame) {
          throw new ValidationError(
            "The model does not support passing a last frame without a first frame.",
          );
        }

        const inputFrameImages = [
          ...(firstFrame ? [firstFrame] : []),
          ...(lastFrame ? [lastFrame] : []),
        ];

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          duration: 8,
          width: size.width,
          height: size.height,
          ...(inputFrameImages.length > 0
            ? { inputs: { frameImages: inputFrameImages } }
            : {}),
        };
      },
    },
    {
      provider: "vertex",
      modelId: "veo-3.1-generate-001",
      estimateCost() {
        return Math.ceil(VEO31_VERTEX_COST_PER_SECOND * 8);
      },
      estimateCostPerSecond() {
        return VEO31_VERTEX_COST_PER_SECOND;
      },
      costPerSecond: VEO31_VERTEX_COST_PER_SECOND,
      toPayload(params) {
        const firstFrame = params.attachments?.find(
          (attachment) => attachment.slot === "first_frame",
        )?.data;
        const lastFrame = params.attachments?.find(
          (attachment) => attachment.slot === "last_frame",
        )?.data;

        if (lastFrame && !firstFrame) {
          throw new ValidationError(
            "The model does not support passing a last frame without a first frame.",
          );
        }

        return {
          model: this.modelId,
          prompt: params.prompt,
          config: {
            aspectRatio: params.aspectRatio,
            durationSeconds: 8,
            ...(lastFrame ? { lastFrame: { url: lastFrame } } : {}),
          },
          ...(firstFrame ? { image: { url: firstFrame } } : {}),
        };
      },
    },
  ],
});
